/**
 * O que faz: calcula o tempo até fechar (dias entre o primeiro contato do lead e a primeira venda),
 *            com mediana (P50), P75, P90, faixas de tempo e motivos de exclusão rastreáveis.
 * Onde usa: bloco "Tempo até fechar" em /vendas e cartão de resumo do dashboard.
 * Conexões: tempoFechamentoService (datas da jornada) e listas de vendas já carregadas.
 * Tabelas: nenhuma consulta; recebe vendas_realizadas, leads e metricas_conversas já lidos com RLS.
 * Data: 05/10/2026.
 *
 * Definição da métrica (rastreável até a origem):
 * - Início: o primeiro contato conhecido do lead. Usa a primeira mensagem confirmada
 *   (metricas_conversas.primeiro_contato_em) e a entrada no CRM (leads.created_at), o que vier antes.
 *   Lead importado não usa a data de entrada, porque ela é a data da importação, não do contato.
 * - Fim: a data da PRIMEIRA venda do lead (vendas_realizadas.data_venda). Consulta paga não conta:
 *   é outra etapa do funil e nem toda clínica usa.
 * - Recompra fica de fora: se o lead já tinha comprado antes do período, não é "fechamento".
 * - A contagem é em dias corridos no fuso da clínica (mesmo dia = 0).
 */

const FUSO_CLINICA = 'America/Sao_Paulo';
// Âncora do bloco em /vendas; o cartão do dashboard linka para ela sem importar o componente de Vendas.
export const ID_BLOCO_TEMPO_FECHAMENTO = 'tempo-ate-fechar';
const MILISSEGUNDOS_POR_DIA = 86_400_000;

// Abaixo disso a mediana oscila demais (uma venda muda tudo) e mostrar o número enganaria.
export const MINIMO_VENDAS_MEDIANA = 5;
// P75 e P90 olham a "cauda": com menos de 10 vendas o P90 é praticamente a venda mais lenta.
export const MINIMO_VENDAS_PERCENTIS_ALTOS = 10;

export interface VendaParaTempoFechamento {
  leadId: string;
  dataVenda: string;
}

export interface JornadaLeadTempoFechamento {
  // Primeiro contato conhecido (ISO) ou null quando não há data confiável.
  inicioJornada: string | null;
  // O lead já tinha venda antes do início do período analisado (recompra).
  comprouAntes: boolean;
}

export interface AmostraTempoFechamento<T extends VendaParaTempoFechamento = VendaParaTempoFechamento> {
  venda: T;
  dias: number;
}

export interface ExclusoesTempoFechamento {
  recompra: number;
  semInicio: number;
  dataInconsistente: number;
}

export interface FaixaTempoFechamento {
  rotulo: string;
  minimo: number;
  maximo: number | null;
  quantidade: number;
  percentual: number;
}

export interface ResumoTempoFechamento {
  quantidade: number;
  p50: number | null;
  p75: number | null;
  p90: number | null;
  percentualAteSeteDias: number | null;
  faixas: FaixaTempoFechamento[];
}

// Faixas crescentes: a maioria fecha rápido e poucos demoram muito (cauda longa).
// Faixas iguais (ex.: de 10 em 10 dias) espremeriam quase tudo na primeira barra.
const FAIXAS: Array<Pick<FaixaTempoFechamento, 'rotulo' | 'minimo' | 'maximo'>> = [
  { rotulo: 'Mesmo dia', minimo: 0, maximo: 0 },
  { rotulo: '1–3 dias', minimo: 1, maximo: 3 },
  { rotulo: '4–7 dias', minimo: 4, maximo: 7 },
  { rotulo: '8–14 dias', minimo: 8, maximo: 14 },
  { rotulo: '15–30 dias', minimo: 15, maximo: 30 },
  { rotulo: '31–60 dias', minimo: 31, maximo: 60 },
  { rotulo: '61–90 dias', minimo: 61, maximo: 90 },
  { rotulo: '90+ dias', minimo: 91, maximo: null },
];

const formatadorDiaCivil = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSO_CLINICA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// Converte um instante para o número do dia civil em São Paulo.
// Motivo: a venda é gravada ao meio-dia do dia escolhido e a mensagem tem hora exata;
// comparar instantes daria "-0,3 dia" numa venda fechada no mesmo dia do contato.
function numeroDiaCivil(iso: string): number | null {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  const [ano, mes, dia] = formatadorDiaCivil.format(data).split('-').map(Number);
  return Date.UTC(ano, mes - 1, dia) / MILISSEGUNDOS_POR_DIA;
}

export function diasCorridosEntre(inicioIso: string, fimIso: string): number | null {
  const inicio = numeroDiaCivil(inicioIso);
  const fim = numeroDiaCivil(fimIso);
  if (inicio === null || fim === null) return null;
  return fim - inicio;
}

/**
 * Escolhe a data de início da jornada a partir das duas fontes disponíveis.
 * Regra: a mais antiga entre primeira mensagem e entrada no CRM; lead importado só usa a mensagem.
 */
export function escolherInicioJornada(dados: {
  primeiroContatoEm: string | null;
  criadoEm: string | null;
  importado: boolean;
}): string | null {
  const candidatas = [dados.primeiroContatoEm, dados.importado ? null : dados.criadoEm]
    .filter((data): data is string => Boolean(data) && !Number.isNaN(new Date(data!).getTime()));
  if (candidatas.length === 0) return null;
  return candidatas.reduce((maisAntiga, data) => (new Date(data) < new Date(maisAntiga) ? data : maisAntiga));
}

/**
 * Transforma vendas em amostras de "dias até fechar", uma por lead (a primeira venda).
 * Vendas repetidas do mesmo lead no período contam uma vez só: o fechamento acontece uma vez.
 */
export function montarAmostrasTempoFechamento<T extends VendaParaTempoFechamento>(
  vendas: T[],
  jornadaPorLead: Map<string, JornadaLeadTempoFechamento>,
): { amostras: AmostraTempoFechamento<T>[]; exclusoes: ExclusoesTempoFechamento } {
  const primeiraVendaPorLead = new Map<string, T>();
  vendas.forEach((venda) => {
    // Sem lead não há jornada para medir; a venda continua no faturamento, só não entra aqui.
    if (!venda.leadId) return;
    const atual = primeiraVendaPorLead.get(venda.leadId);
    if (!atual || new Date(venda.dataVenda) < new Date(atual.dataVenda)) primeiraVendaPorLead.set(venda.leadId, venda);
  });

  const exclusoes: ExclusoesTempoFechamento = { recompra: 0, semInicio: 0, dataInconsistente: 0 };
  const amostras: AmostraTempoFechamento<T>[] = [];
  primeiraVendaPorLead.forEach((venda, leadId) => {
    const jornada = jornadaPorLead.get(leadId);
    // Recompra: medir de novo desde o primeiro contato inflaria o tempo com o histórico antigo.
    if (jornada?.comprouAntes) {
      exclusoes.recompra += 1;
      return;
    }
    if (!jornada?.inicioJornada) {
      exclusoes.semInicio += 1;
      return;
    }
    const dias = diasCorridosEntre(jornada.inicioJornada, venda.dataVenda);
    // Venda registrada com data anterior ao primeiro contato é erro de digitação/importação;
    // contar como zero puxaria a mediana para baixo sem motivo real.
    if (dias === null || dias < 0) {
      exclusoes.dataInconsistente += 1;
      return;
    }
    amostras.push({ venda, dias });
  });

  amostras.sort((a, b) => a.dias - b.dias);
  return { amostras, exclusoes };
}

// Percentil com interpolação linear (mesmo método do percentile_cont do PostgreSQL),
// arredondado para dias inteiros, que é como a pessoa lê o número.
export function percentil(diasOrdenados: number[], fracao: number): number | null {
  if (diasOrdenados.length === 0) return null;
  const posicao = (diasOrdenados.length - 1) * fracao;
  const abaixo = Math.floor(posicao);
  const acima = Math.ceil(posicao);
  const valor = diasOrdenados[abaixo] + (diasOrdenados[acima] - diasOrdenados[abaixo]) * (posicao - abaixo);
  return Math.round(valor);
}

export function resumirTempoFechamento(amostras: Array<Pick<AmostraTempoFechamento, 'dias'>>): ResumoTempoFechamento {
  const dias = amostras.map((amostra) => amostra.dias).sort((a, b) => a - b);
  const quantidade = dias.length;
  const temMediana = quantidade >= MINIMO_VENDAS_MEDIANA;
  const temPercentisAltos = quantidade >= MINIMO_VENDAS_PERCENTIS_ALTOS;

  const faixas = FAIXAS.map((faixa) => {
    const quantidadeFaixa = dias.filter((valor) => valor >= faixa.minimo && (faixa.maximo === null || valor <= faixa.maximo)).length;
    return { ...faixa, quantidade: quantidadeFaixa, percentual: quantidade ? quantidadeFaixa / quantidade : 0 };
  });

  return {
    quantidade,
    p50: temMediana ? percentil(dias, 0.5) : null,
    p75: temPercentisAltos ? percentil(dias, 0.75) : null,
    p90: temPercentisAltos ? percentil(dias, 0.9) : null,
    percentualAteSeteDias: temMediana ? dias.filter((valor) => valor <= 7).length / quantidade : null,
    faixas,
  };
}

/**
 * Posição (0 a 1) de um número de dias no eixo das faixas, para desenhar os marcadores
 * P50/P75/P90 sobre o histograma. Dentro da faixa, a posição é proporcional aos dias.
 */
export function posicaoNasFaixas(dias: number, faixas: Array<Pick<FaixaTempoFechamento, 'minimo' | 'maximo'>> = FAIXAS): number {
  const indice = faixas.findIndex((faixa) => dias >= faixa.minimo && (faixa.maximo === null || dias <= faixa.maximo));
  if (indice < 0) return 0;
  const faixa = faixas[indice];
  // Faixa aberta (90+) não tem fim: o marcador fica no meio da barra.
  const fracao = faixa.maximo === null ? 0.5 : (dias - faixa.minimo + 0.5) / (faixa.maximo - faixa.minimo + 1);
  return (indice + fracao) / faixas.length;
}

/**
 * Agrupa as amostras por origem (campanha, conjunto ou anúncio) e resume cada grupo.
 * Grupos com menos de MINIMO_VENDAS_MEDIANA ficam de fora: mediana de 2 vendas não informa nada.
 */
export function agruparTempoFechamento<T extends VendaParaTempoFechamento>(
  amostras: AmostraTempoFechamento<T>[],
  chaveDe: (venda: T) => { chave: string | null; rotulo: string | null },
) {
  const grupos = new Map<string, { chave: string | null; rotulo: string | null; amostras: AmostraTempoFechamento<T>[] }>();
  amostras.forEach((amostra) => {
    const { chave, rotulo } = chaveDe(amostra.venda);
    const chaveNormalizada = chave?.trim().toLocaleLowerCase('pt-BR') || '';
    const grupo = grupos.get(chaveNormalizada) ?? { chave: chave?.trim() || null, rotulo, amostras: [] };
    grupo.amostras.push(amostra);
    grupos.set(chaveNormalizada, grupo);
  });

  const todos = [...grupos.values()].map((grupo) => ({
    chave: grupo.chave,
    rotulo: grupo.rotulo,
    resumo: resumirTempoFechamento(grupo.amostras),
  }));
  const visiveis = todos
    .filter((grupo) => grupo.resumo.quantidade >= MINIMO_VENDAS_MEDIANA)
    // Mais vendas primeiro: o número mais confiável aparece no topo.
    .sort((a, b) => b.resumo.quantidade - a.resumo.quantidade || (a.resumo.p50 ?? 0) - (b.resumo.p50 ?? 0));
  return { visiveis, ocultos: todos.length - visiveis.length };
}

export function formatarDias(dias: number): string {
  if (dias === 0) return 'mesmo dia';
  return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}
